<script>
    /**
     * Lyme disease cases by county (2002, 2009, 2016, 2023), choropleth shaded by case count.
     * Color scale: D3’s interpolateYlOrBr, stepped into six equal 10-case bins (&lt;10 … 50+).
     */
    import mapboxgl from 'mapbox-gl';
    import { interpolateYlOrBr } from 'd3-scale-chromatic';
    import { onMount, untrack } from 'svelte';

    /** Pause between year steps in the intro / replay animation. */
    const YEAR_CYCLE_MS = 1000;
    /** Full passes through YEARS for the initial intro (before replay enables). */
    const YEAR_CYCLE_LOOPS = 3;
    /** Replay button runs one pass through YEARS only. */
    const REPLAY_CYCLE_LOOPS = 1;
    import rawLymeCsv from './data/LD_Case_Counts_by_County_2023_updated.csv?raw';
    /** Vendored from Plotly datasets (US counties, ~3.2 MB). */
    import countiesBase from './data/us_counties_geojson.json';
    /** Vendored from PublicaMundi MappingAPI (state polygons → line outlines). */
    import statesBase from './data/us_states_geojson.json';
    import { parseLymeCsv, getCaseCount, getCaseInfo } from './utils/parseLymeCsv.js';
    import { getCaseCountForMap } from './utils/ctPlanningRegionCases.js';
    import usContinentalIcon from './img/us.svg?url';
    import meta from './data/meta.json';

    let {
        width = 600,
        loadedData: _loadedData,
        params = {},
        onMapChange: _onMapChange,
        forecast: _forecast,
        manifestData: _manifestData,
        copy: _copy,
        /** Set by App when pym/IO says the embed is in view; gates the intro year animation. */
        yearAnimationAllowed = false,
    } = $props();

    /** No reported cases (0), missing data, or blank cell for that year. */
    const ZERO_OR_NO_DATA_FILL = '#DEDEDE';

    /**
     * County interior borders; stroke color matches each county’s fill (see FILL_COLOR).
     * Mapbox fill layers cannot set outline width; a line layer controls width when &gt; 0.
     */
    const COUNTY_OUTLINE_WIDTH = 0;
    /** State boundary strokes (drawn above counties). Charcoal reads on pale YlOrBr; white washes out. */
    const STATE_LINE_COLOR = '#BCBCBC';
    const STATE_LINE_WIDTH = 1;
    /** Match mapHeight / fitPadding breakpoint — thinner on small viewports. */
    const STATE_LINE_WIDTH_MOBILE = 0.5;

    const rowsByFips = parseLymeCsv(rawLymeCsv);
    const YEARS = [2002, 2009, 2016, 2023];

    const STATE_FIPS_TO_NAME = Object.fromEntries(
        statesBase.features.map((f) => [String(f.id).padStart(2, '0'), f.properties?.name ?? ''])
    );

    const CHOROPLETH_FILL_LAYER_ID = 'lyme-choropleth-fill';
    /** Popup + hover outline only when the choropleth year is 2023. */
    const YEAR_FOR_COUNTY_POPUP = 2023;
    const HOVER_LINE_LAYER_ID = 'lyme-choropleth-hover';
    /** Single-feature source for hover outline (reliable vs feature-state on the full county set). */
    const HOVER_HIGHLIGHT_SOURCE_ID = 'lyme-hover-highlight';

    /** Thinner stroke at national scale; zoom ramps keep edges visible when zoomed in. */
    const HOVER_LINE_WIDTH_EXPR = [
        'interpolate',
        ['linear'],
        ['zoom'],
        2,
        2.5,
        3,
        2,
        4,
        1.6,
        5,
        1.35,
        7,
        1,
        9,
        0.8,
        12,
        0.65,
        14,
        0.55,
    ];

    /** Same bin logic as the choropleth fill, for matching stroke to fill luminance. */
    function choroplethFillColorForCases(cases) {
        if (cases <= 0) return ZERO_OR_NO_DATA_FILL;
        if (cases < 10) return CASE_STEP_COLORS[0];
        if (cases < 20) return CASE_STEP_COLORS[1];
        if (cases < 30) return CASE_STEP_COLORS[2];
        if (cases < 40) return CASE_STEP_COLORS[3];
        if (cases < 50) return CASE_STEP_COLORS[4];
        return CASE_STEP_COLORS[5];
    }

    function parseColorToRgb(colorStr) {
        const s = String(colorStr).trim();
        if (s.startsWith('#')) {
            const h = s.slice(1);
            const n =
                h.length === 3
                    ? parseInt(
                          h
                              .split('')
                              .map((c) => c + c)
                              .join(''),
                          16,
                      )
                    : parseInt(h, 16);
            return { r: (n >> 16) & 255, g: (n >> 8) & 255, b: n & 255 };
        }
        const m = s.match(/rgba?\(\s*([\d.]+)\s*,\s*([\d.]+)\s*,\s*([\d.]+)/);
        if (m) return { r: +m[1], g: +m[2], b: +m[3] };
        return { r: 222, g: 222, b: 222 };
    }

    /** WCAG relative luminance; used to pick dark vs light outline on the fill. */
    function relativeLuminanceSrgb(colorStr) {
        const { r, g, b } = parseColorToRgb(colorStr);
        const lin = [r, g, b].map((c) => {
            c = c / 255;
            return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
        });
        return 0.2126 * lin[0] + 0.7152 * lin[1] + 0.0722 * lin[2];
    }

    function hoverStrokeColorForFill(fillColorStr) {
        return relativeLuminanceSrgb(fillColorStr) > 0.45 ? '#262626' : '#ffffff';
    }

    /** Clears county popup + hover line; set in setupCountyPopupInteractions. */
    let clearLymeCountyInspect = () => {};

    let mapContainer = $state();
    let map = $state(null);
    let userHasInteracted = $state(false);
    let styleReady = $state(false);
    let selectedYear = $state(YEARS[0]);
    /** True after YEAR_CYCLE_LOOPS full passes through YEARS; enables replay. */
    let replayEnabled = $state(false);
    let yearCycleTimer = null;
    /** Ensures startYearCycle runs once when yearAnimationAllowed becomes true. */
    let yearIntroStarted = $state(false);

    /** Camera at page load (after initial fit or URL params); reset restores this. */
    let homeExtent = $state(null);
    /** True when the view differs from `homeExtent` (zoom/pan away from load state). */
    let resetExtentEnabled = $state(false);
    /** Ignore move/zoom events while applying the initial continental fit (avoids races with `homeExtent`). */
    let applyingHomeFit = false;

    const mapHeightPx = $derived(width < 640 ? 300 : 500);

    const MAP_STYLE = 'mapbox://styles/cnndigital/cmn7tpfam001501ry0svkfvgq';

    const CONTERMINOUS_US_BOUNDS = [
        [-124.7844079, 24.396308],
        [-66.9513812, 49.384358],
    ];

    const ALBERS_US = {
        name: 'albers',
        center: [-96, 37.5],
        parallels: [29.5, 45.5],
    };

    /** Skip the palest end of YlOrBr; six evenly spaced t values from tMin … 1. */
    const YL_OR_BR_T_MIN = 0.1;
    const CASE_STEP_COLORS = Array.from({ length: 6 }, (_, i) =>
        interpolateYlOrBr(YL_OR_BR_T_MIN + (i / 5) * (1 - YL_OR_BR_T_MIN)),
    );
    /** Segment tooltips (six bins). */
    const CASE_STEP_TITLES = [
        'Fewer than 10 reported cases',
        '10–19 reported cases',
        '20–29 reported cases',
        '30–39 reported cases',
        '40–49 reported cases',
        '50 or more reported cases',
    ];
    /** Tick labels centered on internal boundaries between color steps. */
    const LEGEND_BOUNDARY_LABELS = ['10', '20', '30', '40', '50+'];

    /** Counties with cases &gt; 0: equal 10-case bins; top bin 50+. */
    const CHOROPLETH_STEP_COLOR = [
        'step',
        ['get', 'cases'],
        CASE_STEP_COLORS[0],
        10,
        CASE_STEP_COLORS[1],
        20,
        CASE_STEP_COLORS[2],
        30,
        CASE_STEP_COLORS[3],
        40,
        CASE_STEP_COLORS[4],
        50,
        CASE_STEP_COLORS[5],
    ];

    /** Grey when cases are 0 or unknown; YlOrBr steps only when cases &gt; 0. */
    const FILL_COLOR = [
        'case',
        ['>', ['get', 'cases'], 0],
        CHOROPLETH_STEP_COLOR,
        ZERO_OR_NO_DATA_FILL,
    ];

    function featureFips(feature) {
        if (feature.id != null) {
            return String(feature.id).padStart(5, '0');
        }
        const geo = feature.properties && feature.properties.GEO_ID;
        if (geo) {
            return String(geo).replace('0500000US', '');
        }
        return null;
    }

    function ringAvg(ring) {
        let sx = 0;
        let sy = 0;
        const n = ring.length;
        if (!n) return null;
        for (const pt of ring) {
            sx += pt[0];
            sy += pt[1];
        }
        return [sx / n, sy / n];
    }

    /** Rough [lng, lat] inside the polygon for anchoring a popup (largest ring for multipolygons). */
    function centroidOfFeature(feature) {
        const geom = feature.geometry;
        if (!geom) return null;
        if (geom.type === 'Polygon') {
            return ringAvg(geom.coordinates[0]);
        }
        if (geom.type === 'MultiPolygon') {
            let bestRing = null;
            let bestArea = 0;
            for (const poly of geom.coordinates) {
                const ring = poly[0];
                let a = 0;
                for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
                    a += ring[j][0] * ring[i][1] - ring[i][0] * ring[j][1];
                }
                a = Math.abs(a / 2);
                if (a > bestArea) {
                    bestArea = a;
                    bestRing = ring;
                }
            }
            return bestRing ? ringAvg(bestRing) : null;
        }
        return null;
    }

    function formatCountyName(feature) {
        const p = feature.properties || {};
        const name = p.NAME || 'Unknown';
        const lsad = String(p.LSAD || '').trim();
        return lsad ? `${name} ${lsad}` : name;
    }

    function stateNameForCountyFeature(feature) {
        const code = String(feature.properties?.STATE ?? '').padStart(2, '0');
        return STATE_FIPS_TO_NAME[code] || '';
    }

    function escapeHtml(s) {
        return String(s)
            .replace(/&/g, '&amp;')
            .replace(/</g, '&lt;')
            .replace(/>/g, '&gt;')
            .replace(/"/g, '&quot;');
    }

    /** County popup + hover outline (2023 only, cases &gt; 0). Map-level hit tests; centroid anchoring. */
    function setupCountyPopupInteractions(mapInstance) {
        let countyPopup = null;
        let lastFips = null;
        /** Last FIPS we applied to the hover line layer — avoid setFilter every mousemove (very expensive). */
        let lastOutlineFips = null;
        let moveRafId = null;
        let lastMoveEvent = null;
        let hideDelayId = null;

        const queryOpts = { layers: [CHOROPLETH_FILL_LAYER_ID] };
        const HIDE_DEBOUNCE_MS = 48;
        /** Nudge popup anchor upward in screen space so the tooltip sits above the cursor/centroid. */
        const POPUP_ANCHOR_UP_PX = 12;

        function popupAnchorLngLat(lngLat) {
            if (!lngLat) return null;
            const pt = mapInstance.project(lngLat);
            return mapInstance.unproject({ x: pt.x, y: pt.y - POPUP_ANCHOR_UP_PX });
        }

        function cancelScheduledHide() {
            if (hideDelayId != null) {
                clearTimeout(hideDelayId);
                hideDelayId = null;
            }
        }

        function setHoverHighlight(fips) {
            const src = mapInstance.getSource(HOVER_HIGHLIGHT_SOURCE_ID);
            if (!src || !mapInstance.getLayer(HOVER_LINE_LAYER_ID)) return;
            if (fips === lastOutlineFips) return;

            if (!fips) {
                lastOutlineFips = null;
                src.setData({ type: 'FeatureCollection', features: [] });
                return;
            }

            const base = countiesBase.features.find((cf) => featureFips(cf) === fips);
            if (!base) {
                lastOutlineFips = null;
                src.setData({ type: 'FeatureCollection', features: [] });
                return;
            }

            lastOutlineFips = fips;
            src.setData({
                type: 'FeatureCollection',
                features: [
                    {
                        type: 'Feature',
                        properties: { fips },
                        geometry: base.geometry,
                    },
                ],
            });

            const row = rowsByFips.get(fips);
            const cases = row ? getCaseCount(row, YEAR_FOR_COUNTY_POPUP) : 0;
            const fillStr = choroplethFillColorForCases(cases);
            const stroke = hoverStrokeColorForFill(fillStr);
            mapInstance.setPaintProperty(HOVER_LINE_LAYER_ID, 'line-color', stroke);
        }

        function clearCountyInspectUI() {
            cancelScheduledHide();
            countyPopup?.remove();
            countyPopup = null;
            lastFips = null;
            mapInstance.getCanvas().style.cursor = '';
            setHoverHighlight(null);
        }

        clearLymeCountyInspect = clearCountyInspectUI;

        function buildPopupHtml(feature) {
            const fips = featureFips(feature);
            const row = fips ? rowsByFips.get(fips) : null;
            const { cases, hasData } = row
                ? getCaseInfo(row, YEAR_FOR_COUNTY_POPUP)
                : { cases: 0, hasData: false };
            const countyLine = escapeHtml(formatCountyName(feature));
            const stateName = escapeHtml(stateNameForCountyFeature(feature));
            const casesNum = hasData ? escapeHtml(cases.toLocaleString()) : '—';
            const casesLine = `<span class="lyme-popup-cases-label">Reported cases:</span> <strong>${casesNum}</strong>`;
            return `<div class="lyme-popup-inner"><div class="lyme-popup-place"><strong>${countyLine}</strong>,<br>${stateName}</div><div class="lyme-popup-cases">${casesLine}</div></div>`;
        }

        function ensurePopup() {
            if (!countyPopup) {
                countyPopup = new mapboxgl.Popup({
                    closeButton: false,
                    closeOnClick: false,
                    maxWidth: '260px',
                    className: 'lyme-county-popup',
                    /* Omit anchor: Mapbox picks one so the popup stays inside the map container. */
                });
            }
            return countyPopup;
        }

        function countyEligibleForPopup(feature) {
            const fips = featureFips(feature);
            const row = fips ? rowsByFips.get(fips) : null;
            const { cases, hasData } = row
                ? getCaseInfo(row, YEAR_FOR_COUNTY_POPUP)
                : { cases: 0, hasData: false };
            return hasData && cases > 0;
        }

        function onPointerMoveOverMap(e) {
            if (selectedYear !== YEAR_FOR_COUNTY_POPUP) {
                return;
            }

            const feats = mapInstance.queryRenderedFeatures(e.point, queryOpts);
            if (!feats.length) {
                cancelScheduledHide();
                hideDelayId = setTimeout(() => {
                    hideDelayId = null;
                    clearCountyInspectUI();
                }, HIDE_DEBOUNCE_MS);
                return;
            }

            cancelScheduledHide();

            const feature = feats[0];
            const fips = featureFips(feature);

            if (!countyEligibleForPopup(feature)) {
                mapInstance.getCanvas().style.cursor = '';
                setHoverHighlight(null);
                cancelScheduledHide();
                countyPopup?.remove();
                countyPopup = null;
                lastFips = null;
                return;
            }

            mapInstance.getCanvas().style.cursor = 'pointer';
            setHoverHighlight(fips);

            if (fips === lastFips) return;

            const centroid = centroidOfFeature(feature);
            if (!centroid) return;
            const lngLat = popupAnchorLngLat(centroid);
            if (!lngLat) return;

            lastFips = fips;
            const html = buildPopupHtml(feature);
            const popup = ensurePopup();
            popup.setLngLat(lngLat).setHTML(html).addTo(mapInstance);
        }

        mapInstance.on('mousemove', (e) => {
            lastMoveEvent = e;
            if (moveRafId != null) return;
            moveRafId = requestAnimationFrame(() => {
                moveRafId = null;
                if (lastMoveEvent) onPointerMoveOverMap(lastMoveEvent);
            });
        });

        mapInstance.on('click', (e) => {
            cancelScheduledHide();
            if (selectedYear !== YEAR_FOR_COUNTY_POPUP) {
                return;
            }

            const feats = mapInstance.queryRenderedFeatures(e.point, queryOpts);
            if (!feats.length) {
                clearCountyInspectUI();
                return;
            }

            const coarse =
                window.matchMedia('(pointer: coarse)').matches ||
                e.originalEvent?.pointerType === 'touch';

            if (!coarse) {
                return;
            }

            const feature = feats[0];
            if (!countyEligibleForPopup(feature)) {
                clearCountyInspectUI();
                return;
            }

            const fips = featureFips(feature);
            const centroid = centroidOfFeature(feature);
            if (!centroid) return;
            const lngLat = popupAnchorLngLat(centroid);
            if (!lngLat) return;

            mapInstance.getCanvas().style.cursor = 'pointer';
            setHoverHighlight(fips);

            lastFips = fips;
            const html = buildPopupHtml(feature);
            const popup = ensurePopup();
            popup.setLngLat(lngLat).setHTML(html).addTo(mapInstance);
        });
    }

    /** Place data under the first symbol layer so basemap labels stay on top. */
    function firstSymbolLayerId(mapInstance) {
        const layers = mapInstance.getStyle()?.layers;
        if (!layers) return undefined;
        for (let i = 0; i < layers.length; i++) {
            if (layers[i].type === 'symbol') {
                return layers[i].id;
            }
        }
        return undefined;
    }

    function mergeCasesIntoCounties(geojson, year) {
        const features = geojson.features.map((f) => {
            const fips = featureFips(f);
            const cases = fips ? getCaseCountForMap(rowsByFips, fips, year) : 0;
            return {
                ...f,
                properties: {
                    ...(f.properties || {}),
                    fips,
                    cases,
                },
            };
        });
        return { type: 'FeatureCollection', features };
    }

    function fitPadding() {
        const cw = mapContainer?.clientWidth ?? width;
        const edge = cw < 640 ? 10 : 14;
        return { top: edge, bottom: edge, left: edge, right: edge };
    }

    function captureHomeFromMap() {
        if (!map) return;
        const c = map.getCenter();
        homeExtent = { lng: c.lng, lat: c.lat, zoom: map.getZoom() };
        resetExtentEnabled = false;
    }

    function isAwayFromHome() {
        if (!map || !homeExtent) return false;
        const c = map.getCenter();
        const z = map.getZoom();
        // Single zoom-step deltas can be under ~0.04 in some projections; keep thresholds small.
        return (
            Math.abs(z - homeExtent.zoom) > 0.001 ||
            Math.abs(c.lng - homeExtent.lng) > 1e-6 ||
            Math.abs(c.lat - homeExtent.lat) > 1e-6
        );
    }

    function refreshResetExtentState() {
        if (!map || !homeExtent || applyingHomeFit) return;
        resetExtentEnabled = isAwayFromHome();
    }

    const RESET_EASE_MS = 1600;

    function resetMapToHome() {
        if (!map || !homeExtent || !resetExtentEnabled) return;
        resetExtentEnabled = false;
        map.easeTo({
            center: [homeExtent.lng, homeExtent.lat],
            zoom: homeExtent.zoom,
            duration: RESET_EASE_MS,
            easing: (t) => 1 - (1 - t) ** 3,
        });
    }

    function fitConterminousUS() {
        if (!map) return;
        applyingHomeFit = true;
        try {
            map.resize();
            const padding = fitPadding();
            const opts = { padding, duration: 0 };

            map.setProjection('mercator');
            map.fitBounds(CONTERMINOUS_US_BOUNDS, opts);
            map.setProjection(ALBERS_US);
            captureHomeFromMap();
        } finally {
            applyingHomeFit = false;
        }
        refreshResetExtentState();
    }

    function scheduleFit() {
        requestAnimationFrame(() => {
            requestAnimationFrame(() => {
                if (!userHasInteracted) {
                    fitConterminousUS();
                }
            });
        });
    }

    /**
     * @param {number} [loops] - How many full passes through YEARS (default: intro YEAR_CYCLE_LOOPS).
     */
    function startYearCycle(loops = YEAR_CYCLE_LOOPS) {
        if (yearCycleTimer) {
            clearInterval(yearCycleTimer);
            yearCycleTimer = null;
        }
        replayEnabled = false;
        selectedYear = YEARS[0];
        let i = 0;
        let loopIndex = 0;
        yearCycleTimer = setInterval(() => {
            i++;
            if (i >= YEARS.length) {
                loopIndex++;
                if (loopIndex >= loops) {
                    clearInterval(yearCycleTimer);
                    yearCycleTimer = null;
                    replayEnabled = true;
                    return;
                }
                i = 0;
                selectedYear = YEARS[0];
                return;
            }
            selectedYear = YEARS[i];
        }, YEAR_CYCLE_MS);
    }

    $effect(() => {
        if (!yearAnimationAllowed || yearIntroStarted) return;
        yearIntroStarted = true;
        startYearCycle(YEAR_CYCLE_LOOPS);
    });

    mapboxgl.accessToken =
        import.meta.env.VITE_MAPBOX_TOKEN ||
        'pk.eyJ1IjoiY25uZGlnaXRhbCIsImEiOiJja2V1M3Vyd3kwMDZrMnF0Njdhb3ZhZDk5In0.OURIYzTcS6ZzoWjXJzfANg';

    onMount(() => {
        if (!mapContainer) {
            return () => {
                if (yearCycleTimer) {
                    clearInterval(yearCycleTimer);
                    yearCycleTimer = null;
                }
            };
        }

        if (params.lat || params.lng || params.zoom) {
            userHasInteracted = true;
        }

        map = new mapboxgl.Map({
            container: mapContainer,
            style: MAP_STYLE,
            projection: ALBERS_US,
            center: [-98, 39],
            zoom: 3,
            // Reduces light halos along vector edges when compositing over the basemap.
            antialias: false,
        });

        map.addControl(
            new mapboxgl.NavigationControl({ showCompass: false }),
            'top-right',
        );

        map.on('moveend', refreshResetExtentState);
        map.on('zoomend', refreshResetExtentState);

        map.on('dragstart', () => {
            userHasInteracted = true;
        });
        map.on('zoomstart', (e) => {
            if (e.originalEvent) userHasInteracted = true;
        });
        map.on('touchstart', () => {
            userHasInteracted = true;
        });
        map.on('dblclick', () => {
            userHasInteracted = true;
        });

        map.on('load', () => {
            styleReady = true;
            if (!userHasInteracted) {
                scheduleFit();
                } else {
                requestAnimationFrame(() => {
                    requestAnimationFrame(() => {
                        captureHomeFromMap();
                    });
                });
            }
        });

            return () => {
            if (yearCycleTimer) {
                clearInterval(yearCycleTimer);
                yearCycleTimer = null;
            }
            map?.off('moveend', refreshResetExtentState);
            map?.off('zoomend', refreshResetExtentState);
            map?.remove();
            map = null;
        };
    });

    /* Resize refit only. untrack(fit) so inner reads of homeExtent in refreshResetExtentState
     * do not subscribe this effect — otherwise updating homeExtent re-runs the effect and
     * fitConterminousUS() loops forever, breaking the year animation and reset button. */
    $effect(() => {
        width;
        mapHeightPx;
        if (map && styleReady && !userHasInteracted) {
            untrack(() => fitConterminousUS());
        }
    });

    /** Narrow viewports: zoom only via NavigationControl (+/−); disable pinch, wheel, and double-click zoom. */
    $effect(() => {
        if (!map || !styleReady) return;
        const desktop = width >= 640;
        if (desktop) {
            map.scrollZoom.enable();
            map.doubleClickZoom.enable();
            map.touchZoomRotate.enable();
        } else {
            map.scrollZoom.disable();
            map.doubleClickZoom.disable();
            map.touchZoomRotate.disable();
        }
    });

    $effect(() => {
        if (!map || !styleReady) return;

        const y = Number(selectedYear);
        const data = mergeCasesIntoCounties(countiesBase, y);

        if (!map.getSource('lyme-choropleth')) {
            const beforeSymbols = firstSymbolLayerId(map);
            map.addSource('lyme-choropleth', {
                type: 'geojson',
                data,
            });
            // Seams under fill: both inserted before symbol layers so labels draw on top.
            map.addLayer(
                {
                    id: 'lyme-choropleth-seams',
                    type: 'line',
                    source: 'lyme-choropleth',
                    layout: { 'line-join': 'round', 'line-cap': 'round' },
                    paint: {
                        'line-color': FILL_COLOR,
                        'line-width': [
                            'interpolate',
                            ['linear'],
                            ['zoom'],
                            2,
                            3.25,
                            3,
                            2.5,
                            4,
                            2,
                            5,
                            1.5,
                            7,
                            1,
                            9,
                            0.65,
                            11,
                            0.4,
                            14,
                            0.25,
                        ],
                        'line-opacity': 1,
                    },
                },
                beforeSymbols,
            );
            map.addLayer(
                {
                    id: CHOROPLETH_FILL_LAYER_ID,
                    type: 'fill',
                    source: 'lyme-choropleth',
                    layout: { 'fill-antialias': false },
                    paint: {
                        'fill-color': FILL_COLOR,
                        'fill-opacity': 1,
                        'fill-outline-color': 'rgba(255,255,255,0)',
                    },
                },
                beforeSymbols,
            );
            if (COUNTY_OUTLINE_WIDTH > 0) {
                map.addLayer(
                    {
                        id: 'lyme-choropleth-outline',
                        type: 'line',
                        source: 'lyme-choropleth',
                        paint: {
                            'line-color': FILL_COLOR,
                            'line-width': COUNTY_OUTLINE_WIDTH,
                            'line-opacity': 1,
                        },
                    },
                    beforeSymbols,
                );
            }
        } else {
            map.getSource('lyme-choropleth').setData(data);
        }

        if (!map.getSource('us-states-outline')) {
            const beforeSymbols = firstSymbolLayerId(map);
            map.addSource('us-states-outline', {
                type: 'geojson',
                data: statesBase,
            });
            map.addLayer(
                {
                    id: 'us-states-outline-line',
                    type: 'line',
                    source: 'us-states-outline',
                    paint: {
                        'line-color': STATE_LINE_COLOR,
                        'line-width': STATE_LINE_WIDTH,
                        'line-opacity': 1,
                        'line-join': 'round',
                        'line-cap': 'round',
                    },
                },
                beforeSymbols,
            );
        }

        if (!map.getSource(HOVER_HIGHLIGHT_SOURCE_ID)) {
            map.addSource(HOVER_HIGHLIGHT_SOURCE_ID, {
                    type: 'geojson',
                data: { type: 'FeatureCollection', features: [] },
            });
        }

        if (!map.getLayer(HOVER_LINE_LAYER_ID)) {
            const beforeSymbols = firstSymbolLayerId(map);
            map.addLayer(
                {
                    id: HOVER_LINE_LAYER_ID,
                    type: 'line',
                    source: HOVER_HIGHLIGHT_SOURCE_ID,
                    layout: {
                        visibility: 'visible',
                        'line-join': 'round',
                        'line-cap': 'round',
                    },
                    paint: {
                        'line-color': '#1a1a1a',
                        'line-opacity': 0.95,
                        'line-blur': 0.35,
                        'line-width': HOVER_LINE_WIDTH_EXPR,
                    },
                },
                beforeSymbols,
            );
            setupCountyPopupInteractions(map);
        }
    });

    $effect(() => {
        if (selectedYear !== YEAR_FOR_COUNTY_POPUP) {
            clearLymeCountyInspect();
        }
    });

    $effect(() => {
        if (!map || !styleReady) return;
        if (!map.getLayer('us-states-outline-line')) return;
        map.setPaintProperty(
            'us-states-outline-line',
            'line-width',
            width < 640 ? STATE_LINE_WIDTH_MOBILE : STATE_LINE_WIDTH,
        );
    });
</script>

<div class="lyme-graphic">
    <div class="legend-toolbar">
        <div class="choropleth-legend">
            <p class="legend-title">Lyme disease cases, by county</p>
            <div class="legend-stack">
                <div class="legend-bar-row">
                    <div class="legend-steps">
                        {#each CASE_STEP_COLORS as color, i}
                            <span
                                class="legend-step"
                                style="background-color: {color};"
                                title={CASE_STEP_TITLES[i]}
                            ></span>
                        {/each}
                    </div>
                    <div class="legend-nodata">
                        <span class="legend-swatch legend-swatch--nodata" title="No reported cases"></span>
                        <span class="legend-label">No reported cases</span>
                    </div>
                </div>
                <div class="legend-boundary-labels">
                    {#each LEGEND_BOUNDARY_LABELS as label, i}
                        <span
                            class="legend-boundary-label"
                            style:left="{((i + 1) / CASE_STEP_COLORS.length) * 100}%"
                        >{label}</span>
                    {/each}
                </div>
            </div>
        </div>
   </div>
   
    <div class="map-wrapper" style="height: {mapHeightPx}px">
        <div
            bind:this={mapContainer}
            class="map-container"
            role="application"
            aria-label="Choropleth map of Lyme disease cases by county"
        ></div>
        <div class="map-year-controls" role="group" aria-label="Year and animation">
            <span class="year-display" aria-live="polite">{selectedYear}</span>
            <button
                type="button"
                class="replay-button"
                disabled={!replayEnabled}
                onclick={() => startYearCycle(REPLAY_CYCLE_LOOPS)}
                aria-label="Replay year animation"
                title={replayEnabled ? 'Replay' : 'Plays when animation finishes'}
            >
                <span class="replay-button__icon" aria-hidden="true">
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" focusable="false">
                        <path
                            fill="currentColor"
                            d="M12 5V1L7 6l5 5V7c3.31 0 6 2.69 6 6s-2.69 6-6 6-6-2.69-6-6H4c0 4.42 3.58 8 8 8s8-3.58 8-8-3.58-8-8-8z"
                        />
    </svg>
                </span>
   </button>
        </div>
        <button
            type="button"
            class="map-reset-extent-btn"
            disabled={!resetExtentEnabled}
            onclick={() => resetMapToHome()}
            aria-label="Reset map to initial view"
            title={resetExtentEnabled ? 'Reset map view' : 'Zoom or pan the map to enable'}
        >
            <span class="map-reset-extent-btn__icon" aria-hidden="true">
                <img
                    class="map-reset-extent-btn__img"
                    src={usContinentalIcon}
                    alt=""
                    width="18"
                    height="18"
                    draggable="false"
                />
            </span>
        </button>
    </div>

    {#if meta.footnote}
        <p class="graphic-footnote">{meta.footnote}</p>
    {/if}
</div>

<style lang="scss">
    /* Matches dailygraphics CNN stacks; CNN Sans Display for UI type. */
    $font-cnn-display:
        'CNN Sans Display', 'CNN', 'CNN Sans', 'Helvetica Neue', Helvetica, Utkal, Arial, sans-serif;

    .lyme-graphic {
        width: 100%;
        font-family: $font-cnn-display;
        font-size: 12px;
    }

    .legend-toolbar {
        margin-bottom: 10px;
    }

    .map-year-controls {
        position: absolute;
        top: 10px;
        left: 10px;
        right: auto;
        z-index: 2;
        display: flex;
        align-items: center;
        gap: 0;
    }

    @media (min-width: 640px) {
        .map-year-controls {
            left: auto;
            right: 10px;
        }
    }

    .year-display {
        font-size: 2rem;
        font-weight: 700;
        line-height: 1;
        min-width: 4.5ch;
        font-variant-numeric: tabular-nums;
        color: #1a1a1a;
    }
    
    .replay-button {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        box-sizing: border-box;
        width: 29px;
        height: 29px;
        padding: 0;
        margin: 0 0 0 -3px;
        color: #333;
        background: #fff;
        border: none;
        border-radius: 6px;
        box-shadow:
            0 0 4px rgba(0, 0, 0, 0.1),
            0 0 10px rgba(0, 0, 0, 0.06);
        cursor: pointer;
        appearance: none;
        -webkit-appearance: none;
    }

    .replay-button__icon {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 18px;
        height: 18px;
        flex-shrink: 0;
    }

    .replay-button__icon svg {
        display: block;
        width: 100%;
        height: 100%;
    }

    .replay-button:disabled {
        opacity: 0.35;
        cursor: not-allowed;
    }

    .replay-button:not(:disabled):hover {
        background: #f5f5f5;
        box-shadow:
            0 0 6px rgba(0, 0, 0, 0.12),
            0 0 14px rgba(0, 0, 0, 0.08);
    }

    /* Desktop: above Mapbox zoom (bottom-right), cleared above attribution. Mobile: top row — see media query. */
    .map-reset-extent-btn {
        position: absolute;
        bottom: 98px;
        right: 10px;
        top: auto;
        left: auto;
        z-index: 2;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        box-sizing: border-box;
        width: 29px;
        height: 29px;
        padding: 0;
        margin: 0;
        background: #fff;
        border: none;
        border-radius: 6px;
        box-shadow:
            0 0 4px rgba(0, 0, 0, 0.1),
            0 0 10px rgba(0, 0, 0, 0.06);
        cursor: pointer;
        appearance: none;
        -webkit-appearance: none;
    }

    .map-reset-extent-btn__icon {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 18px;
        height: 18px;
        flex-shrink: 0;
    }

    .map-reset-extent-btn__img {
        display: block;
        width: 18px;
        height: 18px;
        object-fit: contain;
    }

    .map-reset-extent-btn:disabled {
        opacity: 0.35;
        cursor: default;
    }

    .map-reset-extent-btn:not(:disabled):hover {
        background: #f5f5f5;
        box-shadow:
            0 0 6px rgba(0, 0, 0, 0.12),
            0 0 14px rgba(0, 0, 0, 0.08);
    }

    /* Desktop: Mapbox zoom bottom-right, lifted above default attribution/logo strip. */
    @media (min-width: 640px) {
        .map-wrapper :global(.mapboxgl-ctrl-top-right) {
            top: auto;
            bottom: 20px;
            right: 0;
            left: auto;
        }

        .map-wrapper :global(.mapboxgl-ctrl-top-right .mapboxgl-ctrl) {
            float: right;
            margin: 0 10px 10px 0;
        }
    }

    /* Mobile: year/replay top-right; zoom (+/-) then reset in a row, top-left (zoom left of reset). */
    @media (max-width: 639px) {
        .map-year-controls {
            left: auto;
            right: 10px;
        }

        .map-reset-extent-btn {
            top: 10px;
            left: 75px;
            right: auto;
            bottom: auto;
        }

        .map-wrapper :global(.mapboxgl-ctrl-top-right) {
            left: 0;
            right: auto;
        }

        .map-wrapper :global(.mapboxgl-ctrl-top-right .mapboxgl-ctrl) {
            float: left;
            margin: 10px 0 0 10px;
        }

        .map-wrapper :global(.mapboxgl-ctrl-top-right .mapboxgl-ctrl-group) {
            display: flex;
            flex-direction: row;
        }

        .map-wrapper :global(.mapboxgl-ctrl-top-right .mapboxgl-ctrl-group > button + button) {
            border-top: 0;
            border-left: 1px solid rgba(0, 0, 0, 0.1);
        }
    }

    .choropleth-legend {
        flex: 1 1 220px;
        min-width: 0;
        max-width: 520px;
        color: #444;
    }

    .legend-title {
        margin: 0 0 4px;
        font-weight: 600;
        line-height: 1.25;
        color: #333;
    }

    .legend-stack {
        display: flex;
        flex-direction: column;
        gap: 3px;
        width: 100%;
    }

    .legend-bar-row {
        display: flex;
        flex-direction: row;
        align-items: center;
        gap: 12px;
        width: 100%;
    }

    .legend-nodata {
        display: flex;
        align-items: center;
        gap: 7px;
        flex-shrink: 0;
        max-width: 112px;
    }

    .legend-nodata .legend-label {
        margin-left: -3px;
    }

    @media (max-width: 639px) {
        .year-display {
            font-size: 1.5rem;
        }

        .replay-button {
            margin-left: 0;
        }
    }

    .legend-swatch {
        width: 14px;
        height: 14px;
        border-radius: 2px;
        flex-shrink: 0;
    }

    .legend-swatch--nodata {
        width: 12px;
        height: 12px;
        background: #dedede;
    }

    .legend-steps {
        flex: 0 1 50%;
        max-width: 50%;
        min-width: 0;
        display: flex;
        width: 100%;
        height: 12px;
        border-radius: 2px;
        overflow: hidden;
    }

    .legend-step {
        flex: 1;
        min-width: 0;
    }

    .legend-boundary-labels {
        position: relative;
        width: 50%;
        max-width: 50%;
        min-height: 1.1em;
        color: #555;
        line-height: 1.2;
    }

    .legend-boundary-label {
        position: absolute;
        top: 0;
        transform: translateX(-50%);
        white-space: nowrap;
        font-variant-numeric: tabular-nums;
    }

    .legend-label {
        flex-shrink: 0;
        font-variant-numeric: tabular-nums;
        line-height: 1.25;
    }

    .map-wrapper {
        width: 100%;
        position: relative;
    }
    
    .map-container {
        width: 100%;
        height: 100%;
        overflow: hidden;
        /* If the WebGL surface leaves any unpainted pixels, match no-data grey instead of default white. */
        background: #dedede;
    }
    
    :global(.mapboxgl-ctrl-attrib) {
        font-family: $font-cnn-display;
        font-size: 12px;
    }
    
    :global(.mapboxgl-ctrl-logo) {
        margin: 0 0 2px 2px;
    }

    /* Entire popup subtree must ignore pointer events so the map keeps receiving mousemove. */
    :global(.mapboxgl-popup.lyme-county-popup),
    :global(.mapboxgl-popup.lyme-county-popup *) {
        pointer-events: none;
    }

    :global(.lyme-county-popup .mapboxgl-popup-tip) {
        display: none;
    }

    :global(.lyme-county-popup .mapboxgl-popup-content) {
        font-family: $font-cnn-display;
        font-size: 12px;
        color: #262626;
        padding: 10px 12px;
        border-radius: 4px;
        box-shadow: 0 2px 10px rgba(0, 0, 0, 0.12), 0 1px 2px rgba(0, 0, 0, 0.06);
    }

    :global(.lyme-popup-inner strong) {
        font-weight: 600;
    }

    :global(.lyme-popup-place) {
        line-height: 1.35;
        padding-bottom: 3px;
        margin-bottom: 8px;
        border-bottom: 1px solid rgba(0, 0, 0, 0.1);
    }

    :global(.lyme-popup-cases) {
        margin-top: 0;
    }

    /* Match .legend-boundary-labels */
    :global(.lyme-popup-cases-label) {
        color: #555;
    }

    :global(.lyme-popup-cases strong) {
        color: #262626;
    }
</style>
